import { useCallback, useEffect, useState } from 'react'
import { fetchAppeals } from '.'
import { toCause, type DonationCause } from '../data/donations'

/**
 * The appeals, as a page reads them: loading, loaded, or failed — **never a blank.**
 *
 * Three states and not two, because "there are no appeals" and "we could not ask" are
 * different facts a reader acts on differently: one is the Foundation's, the other is a
 * network's, and the second is worth trying again. `retry` is that second chance.
 *
 * Fetched per mount rather than cached for the session. An appeal closed in Steward should
 * stop taking gifts on the next page a visitor opens, not on their next visit.
 */
export type AppealsState =
  | { status: 'loading' }
  | { status: 'loaded'; causes: DonationCause[] }
  | { status: 'failed'; message: string }

export function useAppeals(): AppealsState & { retry: () => void } {
  const [state, setState] = useState<AppealsState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setState({ status: 'loading' })
    fetchAppeals()
      .then((appeals) => {
        if (live) setState({ status: 'loaded', causes: appeals.map(toCause) })
      })
      .catch((error: unknown) => {
        if (live) setState({ status: 'failed', message: error instanceof Error ? error.message : String(error) })
      })
    return () => {
      live = false
    }
  }, [attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { ...state, retry }
}
